import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, Building2 } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, loading, login, register } = useAuth();

  const [tab, setTab] = useState("login");
  const [role, setRole] = useState("ngo");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      if (user.role === "admin") navigate("/admin-portal", { replace: true });
      else if (user.role === "ngo") navigate("/ngo-dashboard", { replace: true });
      else if (user.role === "corporate") navigate("/corporate-dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (!result.success) {
      setError(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    const result = await register(email, password, name, role);
    setSubmitting(false);
    if (!result.success) {
      setError(result.error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-ix-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-12 font-body" data-testid="auth-page">
      {/* Header */}
      <div className="mb-8 text-center">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-ix-black transition-colors mb-6"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>
        <div className="flex items-center justify-center gap-2.5 mb-2">
          <img src="/logo-icon.png" alt="X" className="w-9 h-9 object-contain" />
          <span className="font-bold text-xl text-ix-black tracking-tight">Impact<span className="text-ix-red-600">X</span> Bridge</span>
        </div>
        <p className="text-sm text-slate-500 mt-1">Secure platform for CSR-NGO collaboration</p>
      </div>

      {/* Auth Card */}
      <Card className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="auth-card">
        <CardHeader className="pb-2">
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid w-full grid-cols-2 bg-slate-100" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Log In</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <CardTitle className="font-heading text-xl text-slate-900">Welcome back</CardTitle>
              <CardDescription className="text-slate-500 mt-1">Enter your credentials to continue</CardDescription>
              <form onSubmit={handleLogin} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 focus-visible:ring-ix-red-600"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5 focus-visible:ring-ix-red-600"
                    data-testid="login-password-input"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button
                  type="submit"
                  className="w-full bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm"
                  disabled={submitting}
                  data-testid="login-form-submit-button"
                >
                  {submitting ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-6">
              <CardTitle className="font-heading text-xl text-slate-900">Create your account</CardTitle>
              <CardDescription className="text-slate-500 mt-1">Get started with ImpactX Bridge</CardDescription>

              {/* Role Selector */}
              <div className="mt-6 mb-2">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">I am signing up as</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole("ngo")}
                    className={`flex items-center gap-2.5 p-3.5 rounded-md border-2 transition-all ${
                      role === "ngo"
                        ? "border-ix-red-600 bg-red-50 text-ix-red-600"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid="role-selector-ngo"
                  >
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-medium">NGO</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole("corporate")}
                    className={`flex items-center gap-2.5 p-3.5 rounded-md border-2 transition-all ${
                      role === "corporate"
                        ? "border-ix-red-600 bg-red-50 text-ix-red-600"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid="role-selector-corporate"
                  >
                    <Building2 className="w-5 h-5" />
                    <span className="text-sm font-medium">Corporate</span>
                  </button>
                </div>
              </div>

              <form onSubmit={handleRegister} className="mt-4 space-y-4" data-testid="signup-form">
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    {role === "ngo" ? "Organization Name" : "Company Name"}
                  </Label>
                  <Input
                    type="text"
                    placeholder={role === "ngo" ? "Your NGO name" : "Your company name"}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1.5 focus-visible:ring-ix-red-600"
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    type="email"
                    placeholder="you@organization.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 focus-visible:ring-ix-red-600"
                    data-testid="signup-email-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Password</Label>
                  <Input
                    type="password"
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5 focus-visible:ring-ix-red-600"
                    data-testid="signup-password-input"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button
                  type="submit"
                  className="w-full bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm"
                  disabled={submitting}
                  data-testid="signup-form-submit-button"
                >
                  {submitting ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardHeader>
        <CardContent className="pt-0" />
      </Card>
    </div>
  );
}
